import { OrderRegisterStatusType, OrderStatusType, OrderFlagType, FiscalPaymentType } from '../../types';
import { Order, TaxRate } from '../../models';
import { I18n } from '../../wrappers';
import { fiscalServiceLegacy } from '../../fiscal';
import moment from 'moment-timezone';
export class OrderLocalizationBa {
    order;
    constructor(order) {
        this.order = order;
    }
    static fiscalStatuses() {
        return [OrderStatusType.paid, OrderStatusType.advance, OrderStatusType.copy];
    }
    shouldPrintReceipt() {
        return false;
    }
    shouldRegister() {
        // External documents (room charges and their correctives) never
        // fiscalize. Guarded here explicitly because the expression below
        // short-circuits on a null register_status before it ever reaches
        // the isFiscal() check (&& binds tighter than ||).
        if (this.order.isExternal())
            return false;
        return !this.order.register_status || this.order.register_status === OrderRegisterStatusType.failed
            && OrderLocalizationBa.fiscalStatuses().includes(this.order.status)
            && this.order.isFiscal();
    }
    getRegisterRequest() {
        const transactionType = this.order.getToPay().toUnit() > 0 ? 'Sale' : 'Refund';
        let invoiceType = 'Normal';
        if (this.order.isProforma())
            invoiceType = 'ProForma';
        else if (this.order.isCopy())
            invoiceType = 'Copy';
        else if (this.order.isTraining())
            invoiceType = 'Training';
        else if (this.order.isAdvance())
            invoiceType = 'Advance';
        const user = this.order.getUser();
        const client = this.order.getClient();
        const payments = [];
        if (!this.order.getPaidCash().isZero()) {
            payments.push({
                amount: this.order.getPaidCash().add(this.order.getChange()).abs().toUnit(),
                paymentType: FiscalPaymentType.Cash
            });
        }
        if (!this.order.getPaidCreditCard().isZero()) {
            payments.push({
                amount: this.order.getPaidCreditCard().abs().toUnit(),
                paymentType: FiscalPaymentType.Card
            });
        }
        if (!this.order.getPaidInvoice().isZero()) {
            payments.push({
                amount: this.order.getPaidInvoice().abs().toUnit(),
                paymentType: FiscalPaymentType.WireTransfer
            });
        }
        if (!this.order.getPaidCheck().isZero()) {
            payments.push({
                amount: this.order.getPaidCheck().abs().toUnit(),
                paymentType: FiscalPaymentType.Check
            });
        }
        if (!this.order.getPaidVoucher().isZero()) {
            payments.push({
                amount: this.order.getPaidVoucher().abs().toUnit(),
                paymentType: FiscalPaymentType.Voucher
            });
        }
        if (!this.order.getPaidMobileMoney().isZero()) {
            payments.push({
                amount: this.order.getPaidMobileMoney().abs().toUnit(),
                paymentType: FiscalPaymentType.MobileMoney
            });
        }
        if (!this.order.getPaidOtherNoncash().isZero()) {
            payments.push({
                amount: this.order.getPaidOtherNoncash().abs().toUnit(),
                paymentType: FiscalPaymentType.Other
            });
        }
        if (this.order.getPaidAdvance().toUnit() != 0 && this.order.getLeftToPay().toRoundedUnit(2) == 0) {
            payments.push({
                amount: 0,
                paymentType: FiscalPaymentType.Cash
            });
        }
        const items = this.order.isSummary()
            ? this.order.paysForOrders()
                ? this.order.paysForOrders().flatMap(order => order.items)
                : []
            : this.order.items;
        const invoice = {
            requestId: this.order.hash,
            invoiceType: invoiceType,
            transactionType: transactionType,
            payment: payments,
            invoiceNumber: this.order.number + '',
            cashier: user?.name || '',
            items: items.map(i => {
                const taxRate = TaxRate.find(i.tax_rate_id);
                const unit = i.unit_of_measure || '';
                const item = {
                    code: i.getProduct()?.code || undefined,
                    name: i.name,
                    quantity: Math.abs(i.getCount()),
                    discount: i.getDiscountPercent(),
                    labels: taxRate?.label ? [taxRate.label] : [],
                    unitPrice: i.getUnitPrice().abs().toRoundedUnit(2),
                    totalAmount: i.getToPay().abs().toRoundedUnit(2),
                    unit: I18n.t(`products.unit_of_measure.${unit}`) !== `products.unit_of_measure.${unit}`
                        ? I18n.t(`products.unit_of_measure.${unit}`)
                        : unit
                };
                if (i.gtin) {
                    item.gtin = i.gtin;
                }
                if (i.comments.length) {
                    item.note = i.comments.join('\n');
                }
                return item;
            })
        };
        const formatToIsoString = (date) => {
            if (!date.isValid())
                return undefined;
            return date.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
        };
        const advancePaidDate = this.order.due_date;
        if (invoice.invoiceType === 'Advance' && invoice.transactionType === 'Sale' && advancePaidDate) {
            const date = formatToIsoString(moment(advancePaidDate, 'YYYY-MM-DD HH:mm:ss'));
            if (date) {
                invoice.dateAndTimeOfIssue = date;
            }
        }
        const refOrder = this.order.refers_to.length ? this.order.refers_to[0] : null;
        if (refOrder) {
            if (refOrder.register_number && refOrder.registered_at) {
                invoice.referentDocumentNumber = refOrder.register_number;
                invoice.referentDocumentDT = formatToIsoString(moment.utc(refOrder.registered_at).tz('Europe/Sarajevo'));
            }
        }
        else if (this.order.flagsInclude(OrderFlagType.ref)) {
            const reference = this.order.getFlag(OrderFlagType.ref);
            invoice.referentDocumentNumber = reference[0];
            if (reference[1]) {
                invoice.referentDocumentDT = formatToIsoString(moment.tz(reference[1], 'YYYY-MM-DD HH:mm:ss', 'Europe/Sarajevo'));
            }
        }
        if (client) {
            invoice.buyer = {
                idNumber: client.id_number,
                name: client.name,
                address: client.address || '',
                postalNumber: client.postal_number || '',
                city: client.city || '',
            };
        }
        return invoice;
    }
    async register() {
        if (!this.shouldRegister()) {
            return { success: false, error: I18n.t('general.action_failed') };
        }
        console.log('Order register ' + this.order.hash);
        const request = this.getRegisterRequest();
        const response = await fiscalServiceLegacy.createInvoice(request);
        this.order.register_request = request;
        return this.handleRegisterResponse(request, response);
    }
    async handleRegisterResponse(request, response) {
        if (!response.success) {
            const error = response.error?.code === 'exception'
                ? I18n.t('order.error.register.exception')
                : response.error?.message;
            // Report the register error
            console.log('Order register error', response);
            Order.errorReporter.reportMessage('Order register error', {
                request: JSON.stringify(request),
                response: JSON.stringify(response),
                orderHash: this.order.hash,
                error,
            });
            return { success: false, error };
        }
        this.order.register_status = OrderRegisterStatusType.success;
        this.order.register_response = response.data || null;
        this.order.registered_at = this.order.last_activity_at;
        this.order.register_number = this.order.number + '';
        return { success: true };
    }
}
const OrderLocalizationBaStatic = OrderLocalizationBa;
//# sourceMappingURL=OrderLocalizationBa.js.map